import { useMemo } from 'react';
import { useQueries, useQuery } from 'react-query';
import api from 'config/api';
import requester from 'utils/requester';
import getLocalized from 'utils/get-localized';
import cutAndAddViewAllItem from 'utils/cut-and-add-view-all-items';
import Platform from 'platform';
import { CONTENT_TYPES } from 'constants/content-types';
import { isEmpty, find, size } from 'lodash';
import { LIST_MAX_SIZE as ITEMS_PER_REQUEST } from 'config';

export const KEY = 'USE_LOAD_COLLECTION';

const isUrl = (url) => /\//.test(url);

const fetchCollection = async (url) => {
  // Сюда также может прийти id коллекции и тогда нужно самостоятелньо обернуть
  // этот ид в урл (это происходит в случае загрузки коллекции со страницы details)
  if (!isUrl(url)) url = `/sb/public/dashboard/elements/${url}`;

  let data = await requester({
    method: 'get',
    url: `${api.host}${url}`,
  });

  // Воля в баннерах промотирует DRM объекты. А на OLD_TV не должно быть DRM объектов
  // Отключить через CMS у себя они не могут, поэтому отключаем вот таким костылём
  if (Platform.getDeviceType() === 'OLD_TV') {
    data = data.filter((item) => item.contentSize !== CONTENT_TYPES.BANNER);
  }

  return data;
};

const loadCollectionContent = async ({ url, id }) => {
  try {
    const data = await requester({
      method: 'get',
      url: `${api.host}${url}`,
      params: {
        size: ITEMS_PER_REQUEST,
        page: 0,
        detailed: true,
      },
      appendParams: false,
    });

    return { id, data };
  } catch (error) {
    console.error(error);
    return { id, data: null };
  }
};

const useLoadCollection = (detailsUrl = '', options) => {
  const {
    data: collections,
    isLoading: isCollectionLoading,
    status,
  } = useQuery([KEY, detailsUrl], () => fetchCollection(detailsUrl), {
    ...options,
    enabled: !!detailsUrl,
    staleTime: 5 * 60 * 60 * 1000,
  });

  const queryResults = useQueries(
    collections?.map((metadata) => {
      const url = metadata.detailsUrl;
      const { id } = metadata.carousel;
      return {
        queryKey: ['COLLECTION_CONTENT_LIST', id],
        queryFn: () => loadCollectionContent({ id, url }),
        staleTime: 5 * 60 * 60 * 1000,
      };
    }) ?? [],
    { enabled: !isEmpty(collections) },
  );

  const isContentLoading = queryResults.some((res) => !!res.isLoading);

  const data = useMemo(() => {
    if (!collections || isContentLoading) return [];

    return collections
      .map((collection) => {
        const { contentSize, carousel } = collection;

        const { data } = find(queryResults, ['data.id', carousel.id]);
        const content = data.data?.content;
        const title = getLocalized(carousel.titles, carousel.title);
        const isNeedToCutItems =
          size(content) >= ITEMS_PER_REQUEST &&
          ![CONTENT_TYPES.BANNER, CONTENT_TYPES.SMALL_BANNER].includes(contentSize);

        return {
          ...collection,
          title,
          content: isNeedToCutItems ? cutAndAddViewAllItem(content, carousel.id) : content,
        };
      })
      .filter((collection) => !isEmpty(collection.content));
  }, [collections, isContentLoading, queryResults]);

  const isLoading = isCollectionLoading || isContentLoading;

  return { data, isLoading, status };
};

export default useLoadCollection;
